import { Link } from "react-router-dom";
import ValueScoreBadge from "./ValueScoreBadge";
import { Star, Award, Sparkles, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const accentForCategory = (cat) => {
  const map = { flower: "card-green", vape: "card-magenta", edible: "card-sunset", concentrate: "card-purple", tincture: "card-cyan", preroll: "card-green", holistic: "card-cyan", mushroom: "card-purple" };
  return map[cat] || "card-green";
};

const stickerForStrain = (strain) => {
  const map = { indica: "sticker-purple", sativa: "sticker-sunset", hybrid: "sticker-green", cbd: "sticker-cyan", herb: "sticker-cyan" };
  return map[strain] || "sticker-green";
};

export default function ProductCard({ p }) {
  const ppg = p.price_per_gram ?? +(p.price / Math.max(p.weight_g, 0.1)).toFixed(2);
  const score = p.value_score ?? 0;
  const { user } = useAuth();
  const { addToCart } = useCart();

  const onAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to start a cart");
      return;
    }
    try {
      await addToCart(p.id, 1);
      toast.success(`Added · ${p.name}`);
    } catch (err) {
      toast.error("Couldn't add to cart");
    }
  };

  return (
    <Link
      to={`/product/${p.id}`}
      data-testid={`product-card-${p.id}`}
      className={`card-x ${accentForCategory(p.category)} flex flex-col overflow-hidden group`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#0B0F0C] relative">
        <img
          src={p.image_url}
          alt={p.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          <span className={`sticker ${stickerForStrain(p.strain)}`}>{p.strain}</span>
          {p.subtype && (
            <span className="sticker sticker-cyan" style={{ transform: "rotate(-1deg)" }}>{p.subtype}</span>
          )}
          {p.is_top_shelf && (
            <span className="sticker sticker-lemon" style={{ transform: "rotate(2deg)" }}>
              <Award size={10} strokeWidth={2.6} /> Top Shelf
            </span>
          )}
          {p.is_sleeper_pick && (
            <span className="sticker sticker-magenta" style={{ transform: "rotate(-3deg)" }}>
              <Sparkles size={10} strokeWidth={2.6} /> Sleeper
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3"><ValueScoreBadge score={score} /></div>
        <button
          data-testid={`product-add-${p.id}`}
          onClick={onAdd}
          aria-label="Add to cart"
          className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-[#15FF73] border-2 border-[#0B0F0C] shadow-[2px_2px_0_#0B0F0C] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        >
          <Plus size={16} strokeWidth={2.6} />
        </button>
      </div>
      <div className="p-5 flex-1 flex flex-col bg-[#FAF6E8]">
        <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#0B0F0C]/50 mb-1">{p.brand || p.category}</div>
        <h3 className="font-display text-2xl font-bold leading-tight mb-2 tracking-tight">{p.name}</h3>
        <div className="flex items-center gap-2 text-xs mb-4">
          <Star size={12} strokeWidth={2.4} className="fill-[#FF7A3D] text-[#FF7A3D]" />
          <span className="font-mono-x font-bold">{(p.rating ?? 0).toFixed(1)}</span>
          <span className="text-[#0B0F0C]/40">·</span>
          <span className="text-[#0B0F0C]/60">{p.review_count ?? 0} reviews</span>
        </div>
        <div className="flex flex-wrap gap-1 mb-4">
          {(p.effects || []).slice(0, 2).map((e) => (
            <span key={e} className="text-[10px] px-2 py-0.5 rounded-full border border-[#0B0F0C] text-[#0B0F0C] uppercase tracking-wider font-mono-x font-bold">
              {e}
            </span>
          ))}
        </div>
        <div className="mt-auto flex items-end justify-between gap-2 pt-3 border-t-2 border-dashed border-[#0B0F0C]/20">
          <div>
            <div className="label-overline text-[#0B0F0C]/50">Price</div>
            <div className="font-display text-3xl font-extrabold tracking-tight">${p.price.toFixed(0)}</div>
          </div>
          <div className="text-right">
            <div className="label-overline text-[#0B0F0C]/50">/g</div>
            <div className="font-mono-x font-bold">${ppg.toFixed(2)}</div>
          </div>
          <div className="text-right">
            <div className="label-overline text-[#0B0F0C]/50">THC</div>
            <div className="font-mono-x font-bold text-[#FF1A8C]">{p.thc?.toFixed(0)}%</div>
          </div>
        </div>
      </div>
    </Link>
  );
}
